import { useId } from "react";
import { Modal, Text, Group, Button, Stack } from "@mantine/core";
import { IconAlertTriangle, IconInfoCircle, IconCircleCheck } from "@tabler/icons-react";

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "info" | "success";
  kind?: "confirm" | "success" | "error";
  loading?: boolean;
  opened: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "info",
  kind = "confirm",
  loading = false,
  opened,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const descriptionId = useId();
  const colorMap = { danger: "red", warning: "yellow", info: "blue", success: "teal" };
  const color = colorMap[variant];
  const Icon = variant === "success" ? IconCircleCheck : variant === "info" ? IconInfoCircle : IconAlertTriangle;

  return (
    <Modal
      opened={opened}
      onClose={() => { if (!loading) onCancel(); }}
      title={title}
      centered
      size="sm"
      zIndex={10000}
      closeOnClickOutside={false}
      closeOnEscape={!loading}
      withCloseButton={false}
      returnFocus
      aria-describedby={descriptionId}
    >
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <Icon size={24} color={`var(--mantine-color-${color}-6)`} style={{ flexShrink: 0, marginTop: 2 }} />
          <Text size="sm" id={descriptionId} role={kind === "error" ? "alert" : kind === "success" ? "status" : undefined} style={{ whiteSpace: "pre-wrap" }}>{message}</Text>
        </Group>
        <Group justify="flex-end" gap="sm">
          {kind === "confirm" && <Button variant="default" onClick={onCancel} disabled={loading} data-autofocus>
            {cancelLabel}
          </Button>}
          <Button color={color} onClick={onConfirm} loading={loading} data-autofocus={kind !== "confirm" || undefined}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
